import React from 'react';
import { useRef } from 'react';
import { Link } from 'gatsby';
import { getExcerpt } from '@storyofams/storyblok-toolkit';
import { useLocation } from '@reach/router';
import FancyTitle from './fancy-title';
import ImagePane from './image-pane';
import { extractLanguage, getStoryblokAlt } from '../helpers/common';

const getStoryblokDimensions = (url) => {
  const [rawWidth, rawHeight] = url?.split('/')[5]?.split('x') || [];
  const width = Number.parseInt(rawWidth, 10);
  const height = Number.parseInt(rawHeight, 10);

  return {
    width: Number.isNaN(width) ? undefined : width,
    height: Number.isNaN(height) ? undefined : height,
  };
};

const Post = ({ post, currentLanguage }) => {
  const title = post.title || post.name || post.slug;
  const featuredImg = post.hero || post.content?.hero?.filename || '';
  const { width: featuredImgWidth, height: featuredImgHeight } = getStoryblokDimensions(featuredImg);

  return (
    <Link
      key={post.uuid}
      to={`/${post.full_slug}`}
      className="mx-auto aspect-[134/85] w-full relative group overflow-hidden"
      aria-label={title}
    >
      <article
        itemScope
        itemType="http://schema.org/Article"
        className="bg-slate-100 dark:bg-latori-green-dark shadow-lg h-full"
      >
        {featuredImg
          && (
          <picture>
            <source
              type="image/avif"
              srcSet={`${featuredImg}/m/300x0/filters:format(avif) 300w, ${featuredImg}/m/450x0/filters:format(avif) 450w, ${featuredImg}/m/600x0/filters:format(avif) 600w, ${featuredImg}/m/800x0/filters:format(avif) 800w`}
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
            />
            <img
              src={`${featuredImg}/m/450x0/filters:format(webp)`}
              srcSet={`${featuredImg}/m/300x0/filters:format(webp) 300w, ${featuredImg}/m/450x0/filters:format(webp) 450w, ${featuredImg}/m/600x0/filters:format(webp) 600w, ${featuredImg}/m/800x0/filters:format(webp) 800w`}
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              alt={getStoryblokAlt(post.content?.hero, '')}
              width={featuredImgWidth}
              height={featuredImgHeight}
              className="w-full h-full object-cover absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
              loading="lazy"
            />
          </picture>
          )}
        <div className="bg-latori-green-dark opacity-90 w-full h-full absolute bottom-0 left-0 transition-[height] group-hover:h-[0%]" />
        <div className="w-full h-full absolute top-0 left-0 flex flex-col justify-center items-start px-4 group-hover:opacity-0 transition-opacity text-white">
          <h2 className="text-2xl text-white drop-shadow-lg" itemProp="headline" aria-hidden="true">{title}</h2>
        </div>
      </article>
    </Link>
  );
};

function BlogGridView(props) {
  let { posts} = props;
  const location = useLocation();
  const gridRef = useRef(null);
  const currentLanguage = extractLanguage(location.pathname);

  if (Object.prototype.hasOwnProperty.call(posts[0], 'node')) {
    posts = posts.map(({ node }) => {
      if (node.content && typeof node.content === 'string') {
        const parsedContent = JSON.parse(node.content);
        return {
          ...node,
          content: parsedContent,
          hero: parsedContent?.hero?.filename || node.field_hero_string,
          title: parsedContent?.title || node.field_title_string || node.name,
        };
      }
      return node;
    }).filter(({ lang }) => lang === currentLanguage);
    if (currentLanguage === 'en') {
      posts = posts.map((post) => ({ ...post, name: post.translated_slugs[0].name }));
    }
  }

  const [firstPost] = React.useState(posts[0]);
  const firstPostHero = firstPost?.hero || firstPost?.content?.hero?.filename;
  const firstPostTitle = firstPost?.title || firstPost?.name || firstPost?.slug;
  const { width: firstPostHeroWidth, height: firstPostHeroHeight } = getStoryblokDimensions(firstPostHero);

  return (
    <>
      <div className="mt-12">
        <ImagePane
          breakpoint="xl"
          image={(
            <Link to={`/${firstPost.full_slug}`} className="w-full">
              {firstPostHero
                && (
                <picture>
                  <source
                    type="image/avif"
                    srcSet={`${firstPostHero}/m/600x0/filters:format(avif) 600w, ${firstPostHero}/m/900x0/filters:format(avif) 900w, ${firstPostHero}/m/1200x0/filters:format(avif) 1200w`}
                    sizes="(max-width: 1024px) 100vw, 60vw"
                  />
                  <img
                    src={`${firstPostHero}/m/900x0/filters:format(webp)`}
                    srcSet={`${firstPostHero}/m/600x0/filters:format(webp) 600w, ${firstPostHero}/m/900x0/filters:format(webp) 900w, ${firstPostHero}/m/1200x0/filters:format(webp) 1200w`}
                    sizes="(max-width: 1024px) 100vw, 60vw"
                    alt={getStoryblokAlt(firstPost?.content?.hero, firstPostTitle)}
                    width={firstPostHeroWidth}
                    height={firstPostHeroHeight}
                    className="w-full"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                  />
                </picture>
                )}
            </Link>
          )}
          title={(
            <Link to={`/${firstPost.full_slug}`}>
              <FancyTitle
                text={currentLanguage === 'en' ? firstPost?.translated_slugs[0].name : firstPostTitle}
                fontSize="text-lg"
                marginBottom="0"
                tag="h2"
              />
            </Link>
          )}
          desktopImageWidth="w-full lg:w-3/5"
          desktopTitleWidth="w-full lg:w-2/5"
          desktopTitleVerticalPosition="center"
          content={
            firstPost.content && firstPost.content.content && (
              <p className="pt-4 leading-6" dangerouslySetInnerHTML={{ __html: (getExcerpt(firstPost.content.content, { maxLength: 140 })) }} />
            )
          }
        />
      </div>

      <div
        className="mt-[60px] lg:mt-20 grid grid-cols-1 xl:grid-cols-3 md:grid-cols-2 gap-2 md:gap-4"
        id="blog-post-list"
        ref={gridRef}
        tabIndex="-1"
        role="region"
        aria-label={currentLanguage === 'en' ? 'Blog post list' : 'Blogbeitragsliste'}
        aria-live="polite"
      >
        {posts.slice(1, posts.length).map((entry) => (
          <Post
            key={entry.uuid}
            post={entry}
            currentLanguage={currentLanguage}
          />
        ))}
      </div>
    </>
  );
}

export default BlogGridView;
